import React, {
    HTMLAttributes, MutableRefObject, ReactElement, ReactNode, useEffect, useState,
} from "react";
import {
    OverlayTrigger, Popover,
} from "react-bootstrap";
import { RiCloseLine } from "react-icons/ri";

import classNames from "classnames";
import { OverlayTriggerType } from "react-bootstrap/esm/OverlayTrigger";
import { RiHistoryLine } from "@remixicon/react";
import { User } from "../../__generated__/api";
import Avatar from "../avatar/Avatar";
import IntegrationUserData from "../integration-user-data/IntegrationUserData";
import Styles from "./UserOverlay.module.scss";
import { useTheme } from "../../contexts/Theme";
import ExpandingSection from "../expanding-section/ExpandingSection";
import ListTickets from "../list-tickets/ListTickets";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";

export function UserDetails({
    user, onToggleShow, className, metricPathPrefix,
}: {
    user: User,
    onToggleShow?: (shouldShow: boolean) => void,
    className?: string;
    metricPathPrefix?: string[];
}): ReactElement {
    const theme = useTheme();

    return (

        <div className={classNames(theme.isV3 ? Styles["card-container"] : "m-n3", className)}>
            <div>
                {onToggleShow && (
                    <button
                        aria-label="Close overlay"
                        className={classNames(
                            theme.isV3 ? "" : "mx-3 ms-n3",
                            `float-end btn btn-none h-0 p-0 `,
                            Styles["user-overlay-close"],

                        )}
                        type="button"
                        onClick={() => onToggleShow(false)}
                    >
                        <RiCloseLine />
                    </button>
                )}

                <div className={`d-flex justify-content-center flex-column mt-2 border-bottom ${Styles["card-header"]}`}>
                    <Avatar src={user.avatarUrl as string} variant="md" className="m-auto" />

                    <h4 className={classNames("text-center mb-2", Styles["user-overlay-name"])}>
                        {user.name}
                    </h4>
                    <div className={classNames("text-center mb-2", Styles["user-overlay-organization"])}>
                        {user.organization?.name ?? user.group}
                    </div>
                </div>
            </div>
            <div className={classNames(Styles["card-body"], "overlay-card-body")}>
                <IntegrationUserData metricPathPrefix={metricPathPrefix} userId={user.id} />
                <ExpandingSection
                    title={(
                        <div className={Styles["title-block"]}>
                            <span className={Styles["title-history-block"]}>
                                <RiHistoryLine />
                            </span>
                            History
                        </div>
                    )}
                    onExpanded={metricPathPrefix
                        ? LoggingHandler.instance.wrapMetric([...metricPathPrefix, "history", "expanded"])
                        : undefined}
                    className="p-0"
                    cardClassName={Styles["history-group-container"]}
                >
                    <ListTickets id={user.id} />
                </ExpandingSection>
            </div>

        </div>
    );
}
UserDetails.defaultProps = {
    onToggleShow: undefined,
    className: undefined,
    metricPathPrefix: undefined,
};

interface PopperProps extends HTMLAttributes<HTMLDivElement> {
    className: string;
    popper?: {scheduleUpdate:() => void};
    children: ReactNode;
}

const UpdatingPopover = React.forwardRef<HTMLDivElement, PopperProps>(
    ({
        popper, children, className, ...props
    }, ref) => {
        useEffect(() => {
            popper?.scheduleUpdate();
        }, [children, popper]);

        return (
            <Popover
                className={className}
                ref={ref as MutableRefObject<HTMLDivElement>}
                body
                {...props}
            >
                {children}
            </Popover>
        );
    },
);
UpdatingPopover.defaultProps = {
    popper: undefined,
};

export default function UserOverlayPopover({
    user, onToggleShow, shouldShow, children, size, placement, trigger, metricPathPrefix,
}: {
    user: User,
    onToggleShow: (shouldShow: boolean) => void,
    shouldShow: boolean,
    children: ReactElement,
    metricPathPrefix?: string[],
    size?: "sm" | "lg",
    placement?: "right" | "left"
    trigger?: OverlayTriggerType
}): ReactElement {
    const isHover = trigger === "hover" || trigger === "focus";
    const [isHoverPopover, setHoverPopover] = useState(false);
    const renderUser = (
        <UpdatingPopover
            onMouseEnter={isHover ? () => setHoverPopover(true) : undefined}
            onMouseLeave={isHover ? () => {
                setTimeout(() => {
                    setHoverPopover(false);
                }, 400);
            } : undefined}
            className={`${Styles["user-card"]} ${size === "lg" ? Styles["user-card-wide"] : ""}`}
            onClick={event => event.stopPropagation()}
        >
            <UserDetails
                user={user}
                metricPathPrefix={metricPathPrefix}
                onToggleShow={value => {
                    onToggleShow(value);
                    setHoverPopover(value);
                }}
            />
        </UpdatingPopover>
    );

    return (
        <OverlayTrigger
            trigger={isHover ? ["hover", "focus"] : trigger}
            placement={placement}
            overlay={renderUser}
            delay={isHover ? { show: 0, hide: 400 } : undefined}
            onToggle={nextShow => {
                onToggleShow(nextShow);
            }}
            show={shouldShow || isHoverPopover}
        >
            {children}
        </OverlayTrigger>

    );
}

UserOverlayPopover.defaultProps = {
    size: "sm",
    placement: "right",
    trigger: "click",
    metricPathPrefix: undefined,
};
